"use client"

import { Link } from "react-router-dom"
import { Bell, PackageCheck, PackagePlus, ScanLine, Trash2, X, XCircle, RefreshCw } from "lucide-react"
import { formatDistanceToNow } from "date-fns"
import { el } from "date-fns/locale"
import { eventMessage, useLiveUpdates } from "@/services/live-updates.tsx"
import type { LiveEvent } from "@/services/api.ts"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu.tsx"

/**
 * Οι σημαντικές αλλαγές, σε ένα σημείο.
 *
 * Ο ιδιοκτήτης ζήτησε «τις σημαντικές»: δημιουργία παραγγελίας, αλλαγή
 * κατάστασης, σάρωση. Ο server καταγράφει ακριβώς αυτές — δες ActivityLog —
 * οπότε εδώ δεν χρειάζεται φιλτράρισμα· ό,τι φτάνει αξίζει να φανεί.
 */

const ICONS: Record<string, typeof Bell> = {
  "order.created": PackagePlus,
  "order.scanned": ScanLine,
  "order.cancelled": XCircle,
  "order.deleted": Trash2,
  "order.status_changed": RefreshCw,
  "materials.received": PackageCheck,
}

const TONES: Record<string, string> = {
  "order.created": "text-emerald-600 dark:text-emerald-400",
  "order.scanned": "text-blue-600 dark:text-blue-400",
  "order.cancelled": "text-amber-600 dark:text-amber-400",
  "order.deleted": "text-destructive",
  "order.status_changed": "text-muted-foreground",
  "materials.received": "text-emerald-600 dark:text-emerald-400",
}

function when(iso: string | null): string {
  if (!iso) return ""
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: el })
  } catch {
    return ""
  }
}

function Row({ e, onDismiss }: { e: LiveEvent; onDismiss: (id: number) => void }) {
  const Icon = ICONS[e.type] ?? Bell
  const body = (
    <div className="flex items-start gap-3 px-3 py-2.5 pr-1">
      <Icon className={`mt-0.5 size-4 shrink-0 ${TONES[e.type] ?? "text-muted-foreground"}`} aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-xs leading-snug text-foreground">{eventMessage(e)}</p>
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          {[e.actor_name, when(e.created_at)].filter(Boolean).join(" · ")}
        </p>
      </div>
    </div>
  )

  return (
    <div className="group/row relative rounded-lg hover:bg-muted/60">
      {/* Μια οριστικά διαγραμμένη παραγγελία δεν έχει πού να οδηγήσει —
          σύνδεσμος προς 404 είναι χειρότερος από καθόλου σύνδεσμος. */}
      {e.order_id ? (
        <Link to={`/orders/${e.order_id}`} className="block">
          {body}
        </Link>
      ) : (
        <div className="opacity-70">{body}</div>
      )}

      {/* Εμφανίζεται στο hover: μόνιμα ορατό «×» σε κάθε γραμμή θα τραβούσε το
          μάτι περισσότερο από την ίδια την ειδοποίηση. */}
      <button
        type="button"
        aria-label="Απόρριψη ειδοποίησης"
        title="Απόρριψη"
        onClick={(ev) => {
          ev.preventDefault()
          ev.stopPropagation()
          onDismiss(e.id)
        }}
        className="absolute right-1 top-1 flex size-6 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus-visible:opacity-100 group-hover/row:opacity-100"
      >
        <X className="size-3.5" />
      </button>
    </div>
  )
}

export function NotificationBell() {
  const { events, unseen, markAllSeen, clearAll, dismiss } = useLiveUpdates()

  return (
    <DropdownMenu onOpenChange={(open) => open && markAllSeen()}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          title="Ειδοποιήσεις"
          aria-label={unseen > 0 ? `Ειδοποιήσεις, ${unseen} νέες` : "Ειδοποιήσεις"}
          className="relative flex size-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <Bell className="size-4" />
          {unseen > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-destructive-foreground">
              {unseen > 9 ? "9+" : unseen}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-80 p-1">
        <div className="flex items-center justify-between gap-2 px-3 py-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Ειδοποιήσεις
            {unseen > 0 && <span className="ml-1.5 font-normal normal-case tracking-normal">({unseen} νέες)</span>}
          </p>
          {events.length > 0 && (
            <button
              type="button"
              onClick={clearAll}
              className="rounded-md px-2 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              Καθαρισμός
            </button>
          )}
        </div>

        {events.length === 0 ? (
          <p className="px-3 py-6 text-center text-xs text-muted-foreground">Καμία δραστηριότητα ακόμα.</p>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            {events.map((e) => (
              <Row key={e.id} e={e} onDismiss={dismiss} />
            ))}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
